import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Handshake, MessageSquare, CheckCheck, Sparkles, Check, Megaphone } from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { Fade } from "@/components/proptech";
import { api, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";

const ICONS = {
  COLLABORATION_REQUEST: Handshake, COLLABORATION_ACCEPTED: Handshake, COLLABORATION_DECLINED: Handshake,
  NEW_MESSAGE: MessageSquare, NEW_PROPERTY_MATCH: Sparkles, NEW_BUYER_MATCH: Sparkles,
  COMMUNICATION: Megaphone,
};

export default function Notifications() {
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);

  const load = () => api.get("/notifications").then((r) => setNotifs(r.data.notifications));
  useEffect(() => { load(); }, []);

  const open = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    if (n.related_entity_type === "collaboration") nav(`/collaboration/${n.related_entity_id}`);
    else if (n.related_entity_type === "referral") nav("/referrals");
    else if (n.related_entity_type === "recruitment") nav(`/recruitment/${n.related_entity_id}`);
    else if (n.related_entity_type === "organization") nav("/connections");
    else if (n.related_entity_type === "connection") nav(n.related_entity_id || "/connections");
    else if (n.related_entity_type === "conversation") nav(n.related_entity_id || "/messages");
    else if (n.related_entity_type === "communication") {
      // Canonical destination is the in-app reader for the campaign.
      const cid = n.related_entity_id;
      if (cid) nav(`/communications/${cid}`); else load();
    }
    else load();
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  const isNew = (n) => !n.is_read;
  const groups = [
    { key: "New", items: notifs.filter(isNew) },
    { key: "Earlier", items: notifs.filter((n) => !isNew(n)) },
  ];

  return (
    <PageShell testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow flex items-center gap-2"><Bell className="h-4 w-4" /> Notifications</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Your activity</h1>
        </div>
        <Button data-testid="mark-all-read" onClick={readAll} variant="outline" className="rounded-xl"><CheckCheck className="h-4 w-4 mr-1.5" /> Mark all read</Button>
      </div>

      {notifs.length === 0 && (
        <div className="bg-slate-50 border border-dashed border-border rounded-2xl p-12 text-center max-w-xl">
          <Bell className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="font-display text-lg font-semibold text-slate-800">No notifications yet</p>
          <p className="text-slate-500 mt-1">Deal events and match alerts will show up here.</p>
        </div>
      )}

      <div className="max-w-2xl space-y-6" data-testid="notifications-list">
        {groups.map((g) => g.items.length > 0 && (
          <div key={g.key}>
            <p className="eyebrow mb-2">{g.key}</p>
            <div className="space-y-2">
              {g.items.map((n, i) => {
                const Icon = ICONS[n.type] || Bell;
                return (
                  <Fade key={n.id} delay={0.03 * i}>
                    <button data-testid={`notif-${n.id}`} onClick={() => open(n)}
                      className={`w-full flex items-start gap-3 text-left rounded-xl border p-4 transition-colors ${isNew(n) ? "bg-accent/5 border-accent/30" : "bg-card border-border"} hover:border-accent`}>
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${isNew(n) ? "bg-accent/15 text-accent" : "bg-secondary text-slate-500"}`}><Icon className="h-5 w-5" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 text-sm flex items-center gap-2">{n.title}{isNew(n) && <span className="h-2 w-2 rounded-full bg-accent" />}</div>
                        <p className="text-sm text-slate-500">{n.message}</p>
                        <span className="text-[11px] text-slate-400">{timeAgo(n.created_at)}</span>
                      </div>
                      {!isNew(n) && <Check className="h-4 w-4 text-slate-300" />}
                    </button>
                  </Fade>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </PageShell>
  );
}
